import type { TFormCustomField } from "components/TitleFormContainer/types";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { ITitle } from "containers/Titles/types";

export type CustomFieldResourceData = {
    customFields?: { customFieldTypeId: number; value: string | number | null }[] | null;
};
import { isDate } from "date-fns";
import type { FieldData, TFieldsDetails } from "formFields";
import { formatLocaleDateString, getJsonOrFalse, isObject } from "helpers/utils";
import { t } from "i18next";
import type { TEntity } from "store/beneficiaryStore";
import type { ICustomField, StoreCustomField } from "store/customFieldsStore";

/**
 * Helps set default values of custom fields within form on creation
 * @param fieldType Type of field (from valueType property on custom field)
 * @returns A default value for the new state of a form.
 */
export const renderDefaultCustomFieldData = (fieldType: string) => {
    switch (fieldType) {
        case "string":
            return "";
        case "date":
            return null;
        case "number":
            return null;
        case "longstring":
            return "";
        case "fileimport":
            return "";
        case "boolean":
            return null;
    }
};

/**
 * Helps set values of custom fields within form on edit
 * @param formData Data from the resource (title or beneficiar)
 * @param field Data of the field from the customFields store
 * @returns Value saved in the form.
 */
export const renderCustomFieldsEditValues = (formData: ITitle | IBeneficiar | CustomFieldResourceData, field: any) => {
    const data = formData.customFields && formData.customFields.find((item) => item.customFieldTypeId === field.id);
    if (data) {
        if (field.valueType === "number") {
            return data.value;
        } else if (field.valueType === "fileimport" && data.value !== "") {
            if (typeof data.value !== "string") return null;
            if (getJsonOrFalse(data.value)) {
                const file = JSON.parse(data.value);
                if (isObject(file)) return file;
            } else {
                return null;
            }
        } else if (field.valueType === "boolean") {
            return data.value == null ? "" : String(data.value);
        } else {
            return data.value ?? "";
        }
    } else {
        return "";
    }
};

/**
 * Within title/beneficiar page, helps fill the block of each section with the custom fields created.
 * @param resourceData Data from the store (title or beneficiar)
 * @param resourceCustomFields Custom fields that exist on the section being populated.
 * @param gridObject Object that is being filled to create data for the block.
 */
export const populateGridWithCustomFields = ({
    resourceData,
    resourceCustomFields,
    gridObject,
}: {
    resourceData: ITitle | IBeneficiar | CustomFieldResourceData;
    resourceCustomFields: ICustomField[];
    gridObject: Record<string, string>;
}) => {
    resourceCustomFields.forEach((field) => {
        const fieldData = resourceData.customFields?.find((item) => item.customFieldTypeId === field.id);
        if (fieldData) {
            let value: any = fieldData.value;
            if (field.valueType === "date") {
                value =
                    typeof fieldData.value === "string"
                        ? formatLocaleDateString({
                              date: fieldData.value,
                              options: { numeric: true, timeZone: "UTC" },
                          })
                        : "-";
            }
            if (field.valueType === "boolean") {
                value = Number(fieldData.value)
                    ? t("CustomFields.CollapseInfo.No")
                    : t("CustomFields.CollapseInfo.Yes");
            }
            gridObject[field.name] = value;
        } else {
            gridObject[field.name] = "-";
        }
    });
};

/**
 * Formats BPortal beneficiary data in order to save changes.
 * @param beneficiaryData All data from benef custom fields
 * @param field Specific custom field being changed.
 * @param fullFieldData Data of CF as saved in store
 */
export const formatFieldDataForPortal = (
    beneficiaryData: StoreCustomField[],
    field: StoreCustomField | undefined,
    fullFieldData: ICustomField | undefined,
    event: any,
) => {
    const newCustomFields = beneficiaryData.map((item) => {
        if (item.customFieldTypeId === field?.customFieldTypeId && fullFieldData) {
            if (["number", "boolean"].includes(fullFieldData.valueType)) {
                return { ...field, value: +event.target.value };
            } else if (fullFieldData.valueType === "fileimport") {
                // In this case event is the fileData already stringified.
                return { ...field, value: event };
            } else {
                return { ...field, value: event.target.value };
            }
        } else {
            return item;
        }
    });
    return newCustomFields;
};

/**
 * Used to format customFields data before saving it:
 * @param customFields Custom fields as saved within form
 * @returns Object with correct format
 */
export const formatCustomFields = (customFields: TFormCustomField[] | undefined, fieldsData: ICustomField[]) => {
    if (!customFields) return;
    const reqObj = {};
    customFields &&
        customFields.forEach((field) => {
            const currentField = fieldsData.find((item) => item.uuid === field.uuid);
            if (isObject(field.value) && !isDate(field.value)) {
                // If there has been no change made to a file, I need to stringify it to be able to save:
                reqObj[field.uuid] = JSON.stringify(field.value);
            } else if (currentField?.valueType === "boolean") {
                return (reqObj[field.uuid] = +field.value);
            } else {
                reqObj[field.uuid] = field.value;
            }
        });
    return reqObj;
};

/**
 * (Needed in BPortal) Ensures that all longstring fields are placed at end of array and so reducing risk that grid is broken (for display purposes)
 * @param customFields
 * @returns Sorted array of custom fields.
 */
export const placeTextAreaAtEnd = (customFields: ICustomField[] | undefined) => {
    return customFields?.sort((a, b) => {
        if (a.valueType === "longstring" && b.valueType !== "longstring") {
            return 1;
        } else if (a.valueType !== "longstring" && b.valueType === "longstring") {
            return -1;
        } else {
            return 0;
        }
    });
};

/**
 * Returns only custom fields that match the beneficiary entity type.
 * @param customFields
 * @param beneficiaryData
 * @returns
 */
export const extractEntityCustomFields = (
    customFields: ICustomField[] | undefined,
    entityType: TEntity | undefined,
) => {
    return customFields?.filter((item) => {
        const isBeneficiaryField = item.relatedResource === "beneficiary";
        const isForAllEntities = item.entityType === "none";
        const isForThisEntity = item.entityType === entityType;
        const beneficiaryHasNoEntity = entityType === "none";
        return isBeneficiaryField && (beneficiaryHasNoEntity || isForThisEntity || isForAllEntities);
    });
};

export const getDisplayedNativeFields = (nativeFields: Record<string, FieldData>) => {
    const displayedFields: Record<string, FieldData> = {};
    for (const [formLocation, section] of Object.entries(nativeFields)) {
        const visibleFields = (section.fields as TFieldsDetails[]).filter((field) => field.display ?? true);
        displayedFields[formLocation] = { ...section, fields: visibleFields };
    }
    return displayedFields;
};

export const formatFieldsForDefaultValues = ({
    customFields,
    editModeActive,
    typeData,
}: {
    customFields: any;
    editModeActive: boolean | undefined;
    typeData: ITitle | IBeneficiar | CustomFieldResourceData | null;
}): TFormCustomField[] => {
    if (editModeActive && !typeData) return [];
    const data: TFormCustomField[] = [];
    customFields.forEach((field) => {
        data.push({
            uuid: field.uuid,
            value:
                editModeActive && typeData
                    ? renderCustomFieldsEditValues(typeData, field)
                    : renderDefaultCustomFieldData(field.valueType),
        });
    });

    return data;
};
